import { ActionIcon, Box, Card, Stack, Text } from "@mantine/core";
import { IMeeting } from "@mctypes/meeting.types";
import { IconTrash } from "@tabler/icons-react";
import React from "react";
import { useHistory } from "react-router-dom";
import { ConfirmDialog, FailedAlert, McDropdown, McDropdownSingleUser, McInput } from "../../../../../components";
import { useAppSelector } from "../../../../../hooks";
import { getProjectIdFromUrl } from "../../../../utils";
import { useGetIssueBoardsQuery } from "../../../issuesApp/IssueBoardRTK";
import { useUpdateMeetingMutation } from "../../meetingRTK";

const MeetingInfo: React.FC<{ singleMeeting: IMeeting.MeetingPopulated }> = ({ singleMeeting }) => {
  const history = useHistory();
  const projectId = getProjectIdFromUrl(history.location.pathname);
  const project = useAppSelector((state) => state.adminReducer.project);

  const { data: issueBoards = [] } = useGetIssueBoardsQuery({
    projectId: projectId,
  });

  const onDelete = () => {
    updateMeeting({
      meetingId: singleMeeting._id,
      projectId,
      attribute: "isDeleted",
      value: true,
    });
    history.push(`/project/${projectId}/meetings`);
  };

  const [updateMeeting, { isError: updateMeetingError }] = useUpdateMeetingMutation();

  const onChange = (attribute: keyof IMeeting.MeetingPopulated, value: string | boolean | Date) => {
    updateMeeting({
      meetingId: singleMeeting._id,
      projectId,
      attribute,
      value,
    });
  };

  return (
    <Box w={500} p="lg">
      <Card p="sm" bd={"1px solid var(--mantine-color-warmgray-2)"}>
        <Stack gap="md">
          {updateMeetingError === true && <FailedAlert />}

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Velg dato for møte
            </Text>
            <McInput
              type="date"
              onChange={(t) => onChange("dateOfMeeting", t.target.value.toString() as any)}
              value={singleMeeting.dateOfMeeting.toString()}
              placeholder="Skriv inn dato for møtet"
            />
          </Stack>

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Starttid for møte
            </Text>
            <McInput
              type="time"
              onBlur={(e) => onChange("timeStart", e.target.value)}
              value={singleMeeting.timeStart}
            />
          </Stack>

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Møtelokasjon
            </Text>
            <McInput
              type="text"
              onBlur={(t) => onChange("location", t.target.value as any)}
              value={singleMeeting.location}
              placeholder="Skriv inn lokasjon for møtet"
            />
          </Stack>

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Eier for møte
            </Text>
            <McDropdownSingleUser
              onChange={(user) => onChange("owner", user?._id)}
              filterActive={project.members?.length > 10}
              options={
                (singleMeeting?.participants as IMeeting.ParticipantsPopulated[]).map(
                  (participant) => participant.user,
                ) ?? []
              }
              selected={singleMeeting.owner}
            />
          </Stack>
          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Referent
            </Text>
            <McDropdownSingleUser
              onChange={(user) => onChange("referent", user?._id)}
              filterActive={project.members?.length > 10}
              options={
                (singleMeeting?.participants as IMeeting.ParticipantsPopulated[]).map(
                  (participant) => participant.user,
                ) ?? []
              }
              selected={singleMeeting.referent}
            />
          </Stack>

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Navn på møte
            </Text>
            <McInput
              type="text"
              onBlur={(t) => onChange("name", t.target.value as any)}
              value={singleMeeting.name}
              placeholder="Skriv inn navn på møtet"
            />
          </Stack>
          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Hensikten med møte
            </Text>
            <McInput
              dynamicHeight
              type="textarea"
              onBlur={(t) => onChange("description", t.target.value as any)}
              value={singleMeeting.description}
              placeholder="Skriv inn beskrivelse"
            />
          </Stack>

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Standard saksregister
            </Text>
            <McDropdown
              disabled={issueBoards.length === 0}
              onClear={singleMeeting.defaultIssueBoard != null ? () => onChange("defaultIssueBoard", null) : null}
              currentValue={singleMeeting.defaultIssueBoard}
              onChange={(oldVal, newVal) => onChange("defaultIssueBoard", newVal._id)}
              options={issueBoards.map((board) => ({ name: board.name, _id: board._id })) ?? []}
              displayKey="name"
            />
          </Stack>

          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Dokumentkode
            </Text>
            <McInput
              dynamicHeight
              type="text"
              onBlur={(t) => onChange("documentCode", t.target.value as any)}
              value={singleMeeting.documentCode}
              placeholder="Skriv inn dokumentkode"
            />
          </Stack>
          <Stack gap={4}>
            <Text fw={500} fz="sm">
              Tilgjengelighet
            </Text>
            <McInput
              dynamicHeight
              type="text"
              onBlur={(t) => onChange("availability", t.target.value as any)}
              value={singleMeeting.availability}
              placeholder="Skriv inn tilgjengelighet"
            />
          </Stack>

          <ConfirmDialog title="Slette møtet?" message="Er du sikker på at du vil slette møtet?" confirmCb={onDelete}>
            <ActionIcon color="red" variant="subtle" aria-label="Slett møte">
              <IconTrash size={18} />
            </ActionIcon>
          </ConfirmDialog>
        </Stack>
      </Card>
    </Box>
  );
};

export default MeetingInfo;
